import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Button, Dialog, Flex, IconButton, Table, Text, TextField, Tooltip } from '@radix-ui/themes'
import { Pencil1Icon, PlusIcon } from '@radix-ui/react-icons'
import { Ops } from '@willie/schemas'
import { FieldShell } from '~/components/FieldShell'
import { TabState } from '~/components/TabState'
import { useTRPC, type TRPCOutputs } from '~/utils/trpc'
import { TabHeader } from './TabHeader'

type MeterMakerRow = TRPCOutputs['ops']['meterMakers']['list'][number]

interface MeterMakerDraft {
  slug: string
  label: string
}

const emptyDraft = (): MeterMakerDraft => ({ slug: '', label: '' })

const toDraft = (maker: MeterMakerRow): MeterMakerDraft => ({ slug: maker.slug, label: maker.label })

export const MeterMakersTab = () => {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editingMaker, setEditingMaker] = useState<MeterMakerRow | null>(null)
  const [draft, setDraft] = useState<MeterMakerDraft>(emptyDraft)
  const [validationError, setValidationError] = useState<string | null>(null)

  const makersQuery = useQuery(trpc.ops.meterMakers.list.queryOptions())
  const invalidateMakers = () => queryClient.invalidateQueries({ queryKey: trpc.ops.meterMakers.list.queryKey() })
  const createMutation = useMutation(trpc.ops.meterMakers.create.mutationOptions({ onSuccess: invalidateMakers }))
  const updateMutation = useMutation(trpc.ops.meterMakers.update.mutationOptions({ onSuccess: invalidateMakers }))

  const openDialog = (maker: MeterMakerRow | null) => {
    setEditingMaker(maker)
    setDraft(maker ? toDraft(maker) : emptyDraft())
    setValidationError(null)
    createMutation.reset()
    updateMutation.reset()
    setDialogOpen(true)
  }

  const draftSlug = editingMaker ? draft.slug : Ops.MeterMakers.toMeterMakerSlug(draft.label)

  const saveMaker = async () => {
    const parsed = Ops.MeterMakers.meterMakerInputSchema.safeParse({ slug: draftSlug, label: draft.label })
    if (!parsed.success) {
      setValidationError(parsed.error.issues[0]?.message ?? 'Check the maker fields.')
      return
    }
    setValidationError(null)
    try {
      await (editingMaker ? updateMutation : createMutation).mutateAsync(parsed.data)
      setDialogOpen(false)
    } catch {
      // Keep the dialog open; the mutation error is displayed below.
    }
  }

  const pending = createMutation.isPending || updateMutation.isPending
  const errorMessage = validationError ?? createMutation.error?.message ?? updateMutation.error?.message ?? null

  return (
    <Flex direction="column" gap="3">
      <TabHeader
        action={
          <Button variant="soft" onClick={() => openDialog(null)}>
            <PlusIcon />
            New maker
          </Button>
        }
      >
        Makers offered on every installation. Renaming one never changes what is stored, so past installations keep
        resolving to it.
      </TabHeader>

      <TabState
        loading={makersQuery.isLoading}
        error={makersQuery.error?.message ?? null}
        isEmpty={makersQuery.data?.length === 0}
        emptyMessage="No meter makers yet."
      >
        <Table.Root variant="surface">
          <Table.Header>
            <Table.Row>
              <Table.ColumnHeaderCell>Name</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Slug</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell />
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {(makersQuery.data ?? []).map((maker) => (
              <Table.Row key={maker.slug}>
                <Table.Cell>
                  <Text size="2" weight="medium">
                    {maker.label}
                  </Text>
                </Table.Cell>
                <Table.Cell>
                  <Text size="2" color="gray" className="font-mono">
                    {maker.slug}
                  </Text>
                </Table.Cell>
                <Table.Cell>
                  <Flex justify="end">
                    <Tooltip content="Edit maker">
                      <IconButton variant="soft" color="gray" size="1" onClick={() => openDialog(maker)}>
                        <Pencil1Icon />
                      </IconButton>
                    </Tooltip>
                  </Flex>
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table.Root>
      </TabState>

      <Dialog.Root open={dialogOpen} onOpenChange={setDialogOpen}>
        <Dialog.Content maxWidth="420px">
          <Dialog.Title>{editingMaker ? 'Edit meter maker' : 'New meter maker'}</Dialog.Title>

          <Flex direction="column" gap="4" mt="4">
            <FieldShell
              label="Name"
              hint={
                <Flex direction="column" gap="1">
                  <Text size="1" color="gray" className="font-mono">
                    {draftSlug || '—'}
                  </Text>
                  <Text size="1" color="gray">
                    Installations store the slug, so it cannot be changed once the maker exists.
                  </Text>
                </Flex>
              }
            >
              <TextField.Root
                autoFocus
                value={draft.label}
                placeholder="e.g. Fuji Electric"
                onChange={(event) => setDraft((current) => ({ ...current, label: event.target.value }))}
              />
            </FieldShell>

            {errorMessage ? (
              <Text size="1" color="red">
                {errorMessage}
              </Text>
            ) : null}
          </Flex>

          <Flex justify="end" gap="2" mt="4">
            <Dialog.Close>
              <Button type="button" variant="soft" color="gray">
                Cancel
              </Button>
            </Dialog.Close>
            <Button type="button" onClick={saveMaker} disabled={pending || !draftSlug}>
              {editingMaker ? 'Save maker' : 'Add maker'}
            </Button>
          </Flex>
        </Dialog.Content>
      </Dialog.Root>
    </Flex>
  )
}
